import { Injectable } from "@angular/core";
import { Subject } from "rxjs";
import { IBusinessFunction } from "../interfaces/ibusinessfunction";
import { IUser } from "../interfaces/ilogin";

@Injectable()
export class AuthService {

    private businessFunctionAnnouncedSource = new Subject<IBusinessFunction>();
    businessFunctionAnnounced$ = this.businessFunctionAnnouncedSource.asObservable();

    private businessFunctionSelectedSource = new Subject<IBusinessFunction[]>();
    businessFunctionSelected$ = this.businessFunctionSelectedSource.asObservable();

    private userLogOutAnnoucedSource = new Subject<boolean>();
    userLogOutAnnouced$ = this.userLogOutAnnoucedSource.asObservable();

    private userSetAnnoucedSource = new Subject<IUser>();
    userSetAnnouced$ = this.userSetAnnoucedSource.asObservable();

    private accessTokenExpiredAnnouncedSource = new Subject<boolean>();
    accessTokenExpiredAnnounced$ = this.accessTokenExpiredAnnouncedSource.asObservable();

    static get businessFunctionId(): number | null {
        if (localStorage.getItem("businessFunction") == null) {
            return null;
        }
        return this.businessFunction?.id || null;
    }

    /**
     * Get the current selected business Function from local storage
     * instead of calling the businessfunction service
     */
    static get businessFunction(): IBusinessFunction | null {
        const bfData = localStorage.getItem("businessFunction");
        if (bfData == null) {
            return null;
        }
        return JSON.parse(bfData) as IBusinessFunction;
    }

    /**
     * Sets the selected business function for the user
     */
    set businessFunction(businessFunction: IBusinessFunction | null) {
        if (businessFunction != null && businessFunction.id != null) {
            localStorage.setItem("businessFunction", JSON.stringify(businessFunction));
        } else {
            localStorage.removeItem("businessFunction");
        }
        this.businessFunctionAnnouncedSource.next(businessFunction as any);
    }

    /**
     * Get the business Functions from local storage instead of calling the businessfunction service
     */
    static get businessFunctions(): IBusinessFunction[] | null {
        const businessFunctionArray: string | null = localStorage.getItem("businessFunctions");
        if (businessFunctionArray == null) {
            return null;
        }
        return JSON.parse(businessFunctionArray) as IBusinessFunction[];
    }

    /**
     * Set the business Functions in local storage for later use
     */
    set businessFunctions(businessFunctions: IBusinessFunction[]) {
        if (businessFunctions != null) {
            localStorage.setItem("businessFunctions", JSON.stringify(businessFunctions));
        } else {
            localStorage.removeItem("businessFunctions");
        }
        this.businessFunctionSelectedSource.next(businessFunctions);
    }

    /**
     * Returns business function based on the default route
     * @param defaultRoute default route of the business function to search for
     */
    static getBusinessFunctionByDefaultRoute(defaultRoute: string): IBusinessFunction | null {
        if (!defaultRoute || this.businessFunctions === undefined || this.businessFunctions == null) {
            return null;
        }

        if (!defaultRoute.startsWith("/")) {
            defaultRoute = `/${defaultRoute}`;
        }
        return this.businessFunctions.find(bf => bf.defaultRoute && bf.defaultRoute.toLowerCase() === `${defaultRoute.toLowerCase()}`) || null;
    }

    /**
     * Sets the current user
     */
    set user(user: IUser | null) {
        if (user) {
            sessionStorage.setItem("currentClientUser", JSON.stringify(user));
            this.userSetAnnoucedSource.next(user);
        } else {
            sessionStorage.removeItem("currentClientUser");
            this.businessFunction = null;
        }
    }

    /**
     * Logs out the selected user
     */
    set logOut(logout: boolean) {
        sessionStorage.removeItem("currentClientUser");
        localStorage.removeItem("businessFunction");
        localStorage.removeItem("accessToken");
        this.userLogOutAnnoucedSource.next(logout);
    }

    set manualLogin(manual: boolean) {
        localStorage.setItem("manualLogin", manual.toString());
    }

    static get manualLogin(): boolean {
        const manualLogin: string | null = localStorage.getItem("manualLogin");
        if (manualLogin && manualLogin === "true") {
            return true;
        }
        return false;
    }

    /**
     * Gets the current user or returns null if not set
     */
    static get user(): IUser | null {
        const userData = sessionStorage.getItem("currentClientUser");
        if (userData == null) {
            return null;
        } else {
            return JSON.parse(userData) as IUser;
        }
    }

    /**
     * Sets the access token from
     */
    set accessToken(accessToken: string | null) {
        if (accessToken != null) {
            localStorage.setItem("accessToken", accessToken);
        } else {
            localStorage.removeItem("accessToken");
        }
    }

    /**
     * Gets the access token for the user
     */
    static get accessToken(): string | null {
        return localStorage.getItem("accessToken");
    }

    /**
     * Sets access token expired
     */
    set accessTokenExpired(expired: boolean) {
        this.accessTokenExpiredAnnouncedSource.next(expired);
    }
}
